import { useCallback, useEffect, useRef, useState } from "react";
import { Action, GeneralAction } from "../components/CowboyEditor/Actions"

/**
 * The properties of the hook.
 */
export type KeyboardActionTriggerHookProps = {
  /**
   * The action triggers to be registered.
   */
  triggers: Array<KeyboardActionTrigger>;

  /**
   * The callback to be called when an action is triggered.
   * @param action The action that was triggered.
   * @param trigger The trigger that caused the action to be triggered.
   * @returns 
   */
  onActionTriggered: (action: GeneralAction, trigger: KeyboardActionTrigger) => void;
}

/**
 * Trigger actions based on PRESSING. Takes precedence over HOLDING.
 */
export type PressActionTrigger = {
  type: 'press';

  /**
   * The key that needs to be pressed to trigger the action.
   */
  key: string;

  /**
   * All modifiers that need to be pressed to trigger the action.
   */
  modifiers: Array<string>;

  /**
   * The action to be triggered.
   */
  action: GeneralAction;
}

/**
 * Trigger actions based on HOLDING a key.
 */
export type HoldActionTrigger = {
  type: 'hold';

  /**
   * The key that needs to be held to trigger the action.
   */
  key: string;

  /**
   * The action to be triggered.
   */
  action: GeneralAction;
}

export type KeyboardActionTrigger = PressActionTrigger | HoldActionTrigger;

export function useKeyboardActionTrigger({ triggers, onActionTriggered }: KeyboardActionTriggerHookProps) {
  // Use useRef instead of useState to avoid stale closures and unnecessary rerenders
  const holdStateRef = useRef<Record<string, number>>({});

  const downHandler = useCallback((event: KeyboardEvent) => {
    triggers.forEach(trigger => {
      if (trigger.type === 'press') {
        const hasKey = event.key === trigger.key;
        const hasModifier = trigger.modifiers.length === 0 ||
          trigger.modifiers.every(modifier => event.getModifierState(modifier));

        if (hasKey && hasModifier) {
          onActionTriggered(trigger.action, trigger);
        }
      } else if (trigger.type === 'hold' && event.key === trigger.key) {
        // Store timestamp instead of Date object
        holdStateRef.current[event.key] = Date.now();
      }
    });
  }, [triggers, onActionTriggered]);

  const upHandler = useCallback((event: KeyboardEvent) => {
    triggers.forEach(trigger => {
      if (trigger.type === 'hold' && event.key === trigger.key) {
        const holdStartTime = holdStateRef.current[event.key];
        if (holdStartTime) {
          const holdDuration = Date.now() - holdStartTime;
          if (holdDuration > 1000) {
            onActionTriggered(trigger.action, trigger);
          }
          // Clean up the hold state
          delete holdStateRef.current[event.key];
        }
      }
    });
  }, [triggers, onActionTriggered]);

  useEffect(() => {
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);

    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, [downHandler, upHandler]);
}